/**
 * Request Error Handler
 * Centralized error handling logic for HTTP requests
 */
import { Button, notification } from 'antd';
import { getIntl } from 'umi';

import checkNetworkStatus from './checkNetworkStatus.js';
import ToastCtxButton from '@/components/Common/ToastContextButton';
import appInsightsService from '@/utils/appInsights';
import { CoreEnums } from '@/utils/domain/constants';
import { parseJson } from '../utils';
import {
  calculateRetryDelay,
  extractRetryFlags,
  isCorsError,
  isTimeoutError,
  shouldRetryError,
  wait,
} from './retryStrategy';
import retryTracker from './retryTracker';
import { handleRedirectResponse } from './requestConfig.js';

let globalRetryContext = null;

export const setGlobalRetryContext = (ctx) => {
  globalRetryContext = ctx;
};

const { ApplicationResponseStatus, ApplicationPage } = CoreEnums;

// Routes that should not display request errors
const ERROR_DISPLAY_CONFIG = {
  omitRoutes: ['/refreshToken', '/logout'],
  disallowedRoutes: [],
  disallowedFragments: ['/request-handle'],
  azureStorageRoutes: [
    'storageMediaManager/generate-sas-token',
    'automytemporary.blob.core.windows.net/temporary-files',
  ],
  allowedFragments: ['/activate'],
  methodsToCheck: ['POST', 'PUT', 'PATCH', 'DELETE'],
};

/**
 * Extract query parameters from URL
 * @param {string} url - URL that may contain query params
 * @returns {object} - Object with query params as key-value pairs
 */
const extractQueryParams = (url) => {
  if (!url) return {};

  try {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) return {};

    const queryString = url.substring(queryIndex + 1);
    if (!queryString) return {};

    const params = {};
    const pairs = queryString.split('&');

    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key) {
        params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
      }
    }

    return params;
  } catch (error) {
    // Invalid URL format - return empty object to prevent crash
    return {};
  }
};

/**
 * Strip query parameters from URL to prevent duplication on retry
 * Query params are extracted and merged separately into options.params
 * @param {string} url - URL that may contain query params
 * @returns {string} - Base URL without query params
 */
const stripQueryParams = (url) => {
  if (!url) return url;
  const queryIndex = url.indexOf('?');
  return queryIndex !== -1 ? url.substring(0, queryIndex) : url;
};

export const shouldDisplayRequestError = ({ url, options }) => {
  if (!url || !options) return true;

  const {
    disallowedRoutes,
    disallowedFragments,
    azureStorageRoutes,
    allowedFragments,
    methodsToCheck,
  } = ERROR_DISPLAY_CONFIG;

  const hasDisallowedRoute = disallowedRoutes.some((route) => url.includes(route));
  const hasDisallowedFragment = disallowedFragments.some((route) => url.includes(route));
  const hasAzureStorageRoute = azureStorageRoutes.some((route) => url.includes(route));

  const isModifyingRequest = methodsToCheck.includes(options?.method);
  const isGetRequest = options?.method === 'GET';

  if (
    (hasDisallowedRoute && isModifyingRequest) ||
    (hasDisallowedFragment && isGetRequest) ||
    (hasAzureStorageRoute && isModifyingRequest)
  ) {
    return allowedFragments.some((fragment) => url.includes(fragment));
  }

  return true;
};

const extractErrorMessage = (data, message) => {
  if (data?.message) return parseJson(data.message)?.Message ?? data.message;
  if (message) return parseJson(message)?.Message ?? message;
  return '';
};

const createNotificationButtons = (responseData, route) => {
  const { page, ...rest } = responseData;

  const isCompanySelector = page === ApplicationPage.CompanySelector;
  const isEnvironmentSelector = page === ApplicationPage.EnvironmentSelector;

  if (!isCompanySelector && !isEnvironmentSelector) return null;

  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      <Button onClick={() => notification.close('glob-notify')}>
        {getIntl().formatMessage({ id: 'global.button.cancel' })}
      </Button>
      {isCompanySelector && <ToastCtxButton {...rest} context="company" route={route} />}
      {isEnvironmentSelector && <ToastCtxButton {...rest} context="environment" route={route} />}
    </div>
  );
};

const displayErrorNotification = (errorText, status, responseData) => {
  if (!errorText) return;

  notification.close('glob-notify');
  const notificationType =
    {
      [ApplicationResponseStatus.Error]: notification.error,
      [ApplicationResponseStatus.Warning]: notification.warning,
      [ApplicationResponseStatus.Information]: notification.info,
    }[status] || notification.error;

  const route = { ...window.location };
  const buttons = createNotificationButtons(responseData, route);

  notificationType({
    key: 'glob-notify',
    description: errorText,
    ...(status === ApplicationResponseStatus.Error && { message: 'Error' }),
    ...(buttons && { duration: 10, btn: buttons }),
  });
};

/**
 * Handle network error (no response)
 * @param {object} error - Error object to check if it's a timeout or CORS
 * @param {boolean} shouldRetry - Flag to know if is a retry able request
 */
const handleNetworkError = async (error = {}, shouldRetry = false) => {
  if (shouldRetry) return;

  let errorText = '';
  const translate = getIntl().formatMessage;
  const { online } = await checkNetworkStatus();

  if (online) {
    if (isTimeoutError(error)) errorText = translate({ id: 'global.text.timeout.info' });
    if (isCorsError(error)) errorText = translate({ id: 'global.text.cors.info' });
  } else errorText = translate({ id: 'global.text.network.info' });

  displayErrorNotification(errorText, ApplicationResponseStatus.Error, {});
};

/**
 * Main error handler for HTTP requests
 * @param {object} error - Error object from request
 * @param {AbortController} abortController - Abort controller for network errors
 * @returns {Promise} - Throws processed error
 */
export const createErrorHandler = (abortController) => async (error) => {
  const { response, data, request } = error;

  let errorText = '';
  let trackingKey = request?.options?.trackingKey;

  if (!trackingKey) {
    trackingKey = retryTracker.generateKey(
      request?.url || error.url || 'unknown',
      request?.options || {},
    );
  }

  // Get current retry count from tracker
  const currentRetryCount = retryTracker.getCount(trackingKey);
  const retryEntry = retryTracker.getEntry(trackingKey);

  if (response) {
    const serverRetryFlags = await extractRetryFlags(response);

    if (response.status === 409) {
      retryTracker.markFailed(trackingKey);
      throw error;
    }

    if (response.status === 301) {
      try {
        const res = await response.clone().json();
        errorText = extractErrorMessage(data, res.message);
        displayErrorNotification(errorText, res.status, res);
        await handleRedirectResponse(response);
      } catch (e) {}

      throw response;
    }

    if (
      response.status >= 400 &&
      shouldDisplayRequestError({ url: request?.url, options: request?.options })
    ) {
      try {
        const res = await response.clone().json();
        errorText = extractErrorMessage(data, res.message);

        appInsightsService.trackHttpError({
          url: request?.url,
          status: response.status,
          method: request?.options?.method,
          errorText,
          responseData: res,
          request,
          retryCount: currentRetryCount,
          maxRetriesReached: retryTracker.isMaxReached(trackingKey),
          serverRetryFlags,
          retryDuration: retryEntry ? Date.now() - retryEntry.firstAttempt : 0,
        });

        const willRetry = await shouldRetryError(
          error,
          currentRetryCount,
          serverRetryFlags,
          request?.options,
        );

        if (!willRetry || (!globalRetryContext && retryTracker.isMaxReached(trackingKey))) {
          displayErrorNotification(errorText, res.status, res);
        }
      } catch (err) {
        appInsightsService.trackHttpError({
          url: request?.url,
          status: response.status,
          method: request?.options?.method,
          error: err,
          request,
          retryCount: currentRetryCount,
        });

        throw response;
      }
    }

    const shouldRetry = await shouldRetryError(
      error,
      currentRetryCount,
      serverRetryFlags,
      request?.options,
    );

    if (!shouldRetry || retryTracker.isMaxReached(trackingKey)) {
      retryTracker.markFailed(trackingKey);
      try {
        const errorData = await response.clone().json();
        throw errorData;
      } catch (parseError) {
        if (parseError instanceof SyntaxError) throw response;
        throw parseError;
      }
    }
  }

  // Skip errors related to aborted requests - check for TypeError on aborted requests
  const isFox = error?.name === 'TypeError' && error?.message?.includes('getter-only property');
  const isAbortError = error?.name === 'AbortError' && error?.message?.includes('is aborted');

  if (isFox || isAbortError) {
    retryTracker.clear(trackingKey);
    throw error;
  }

  if (request?.options?.signal?.aborted) {
    retryTracker.clear(trackingKey);
    throw request.options?.signal;
  }

  const serverRetryFlags = error.response ? await extractRetryFlags(error.response) : null;
  const shouldRetry = await shouldRetryError(
    error,
    currentRetryCount,
    serverRetryFlags,
    request?.options,
  );

  if (!abortController.signal.aborted && shouldRetry && !retryTracker.isMaxReached(trackingKey)) {
    const nextRetryCount = retryTracker.track(trackingKey);
    const delay = calculateRetryDelay(currentRetryCount, serverRetryFlags?.retryAfterSeconds);

    // Notify UI about retry
    if (globalRetryContext && currentRetryCount > 3) {
      const translate = getIntl().formatMessage;
      const requestId = `retry-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

      globalRetryContext.registerRetry(requestId, () => {});
      const { online } = await checkNetworkStatus();

      if (online) {
        if (isTimeoutError(error)) errorText = translate({ id: 'global.text.timeout.info' });
        if (isCorsError(error)) errorText = translate({ id: 'global.text.cors.info' });
      } else errorText = translate({ id: 'global.text.network.info' });

      window.dispatchEvent(
        new CustomEvent('retry-available', {
          detail: {
            retryId: requestId,
            url: request?.url,
            message: errorText,
            retryCount: nextRetryCount,
          },
        }),
      );
    }

    await wait(delay);

    // Extract query params from URL before stripping
    const urlParams = extractQueryParams(request?.url || error.url);
    const baseUrl = stripQueryParams(request?.url || error.url);

    // Merge extracted URL params with options.params, URL params take precedence
    const retryOptions = {
      ...(request?.options || {}),
      trackingKey,
      params: {
        ...(request?.options?.params || {}),
        ...urlParams,
      },
    };

    try {
      const umiRequest = await import('./request');
      const retryResult = await umiRequest.default(baseUrl, retryOptions);

      retryTracker.markSuccess(trackingKey);
      return retryResult;
    } catch (retryError) {
      throw retryError;
    }
  }

  retryTracker.markFailed(trackingKey);

  if (typeof error === 'object' && !abortController.signal.aborted) {
    appInsightsService.trackNetworkError({
      url: request?.url,
      method: request?.options?.method,
      error,
      retryCount: currentRetryCount,
      maxRetriesReached: true,
      serverRetryFlags,
      retryDuration: retryEntry ? Date.now() - retryEntry.firstAttempt : 0,
    });

    if (!currentRetryCount) handleNetworkError(error, false);
  }

  throw {
    ...error,
    retryCount: currentRetryCount,
    maxRetriesReached: true,
    trackingKey,
  };
};
